import { useState } from "react";
import { CButton, CModal, CModalBody, CModalFooter, CModalHeader } from "@coreui/react";
import { useSelector } from "react-redux";
import { useHttp } from "src/hooks";
import { selectUserId } from "src/store/userSlice";

const UserManagementActions = ({ user, orgId, orgName, onRemoved }) => {
  const actorId = useSelector(selectUserId);
  const uid = user.id || user.NO_ID_FIELD;
  const { sendRequest } = useHttp();
  const [action, setAction] = useState(null);
  const [password, setPassword] = useState("");
  const [confirmation, setConfirmation] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const close = () => {
    if (busy) return;
    setAction(null);
    setPassword("");
    setConfirmation("");
    setError("");
  };
  const submit = async (event) => {
    event.preventDefault();
    if (busy) return;
    if (action === "reset" && (password.length < 12 || password.length > 128 || password !== confirmation)) {
      setError("Use 12–128 characters and enter the same password in both fields.");
      return;
    }
    setBusy(true);
    setError("");
    try {
      await sendRequest(action === "reset" ? "/app/users/user/password" : "/app/users/user", {
        method: action === "reset" ? "POST" : "DELETE",
        body: JSON.stringify({ uid, orgId, ...(action === "reset" ? { password } : {}) }),
        successMsg: action === "reset" ? "Password reset. The user must sign in again." : "User removed from this account.",
      });
      setPassword("");
      setConfirmation("");
      setAction(null);
      if (action === "remove") onRemoved?.(uid);
    } catch (err) {
      setError(err.message || "The update failed. Please try again.");
      setPassword("");
      setConfirmation("");
    } finally {
      setBusy(false);
    }
  };

  if (uid === actorId || !orgId) return null;
  return <>
    <CButton size="sm" color="primary" variant="outline" className="mr-2" onClick={() => setAction("reset")}>Reset password</CButton>
    <CButton size="sm" color="danger" variant="outline" onClick={() => setAction("remove")}>Remove user</CButton>
    <CModal show={!!action} onClose={close} closeOnBackdrop={!busy} centered>
      <form onSubmit={submit}>
        <CModalHeader><h4>{action === "reset" ? "Reset password" : "Remove user"}</h4></CModalHeader>
        <CModalBody>
          <p><strong>{user.email}</strong><br />Account: {orgName || orgId}</p>
          {action === "remove" ? <p>Remove this user's access to this account? Historical data will be retained. Access to other accounts will remain. If this is their only account, their login will be deleted.</p> : <>
            <p>This changes the user's password across all their accounts and signs them out. Share the new password securely; no reset email will be sent.</p>
            <label className="d-block">New password
              <input className="form-control" type="password" autoComplete="new-password" minLength={12} maxLength={128} required value={password} disabled={busy} onChange={(e) => setPassword(e.target.value)} />
            </label>
            <small>Use 12–128 characters.</small>
            <label className="d-block mt-3">Confirm new password
              <input className="form-control" type="password" autoComplete="new-password" minLength={12} maxLength={128} required value={confirmation} disabled={busy} onChange={(e) => setConfirmation(e.target.value)} />
            </label>
          </>}
          {error && <p role="alert" className="text-danger mt-3">{error}</p>}
        </CModalBody>
        <CModalFooter>
          <CButton type="button" color="secondary" onClick={close} disabled={busy}>Cancel</CButton>
          <CButton type="submit" color={action === "reset" ? "primary" : "danger"} disabled={busy}>{busy ? "Saving…" : action === "reset" ? "Set new password" : "Remove from account"}</CButton>
        </CModalFooter>
      </form>
    </CModal>
  </>;
};

export default UserManagementActions;
